import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";

const CMSUpdateLayer = ({ id }) => {
  const { AsyncGetApiCall, AsyncPatchApiCall } = useApi();
  const navigate = useNavigate();
  const editor = useRef(null);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
  });

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter CMS description",
      minHeight: 300,
    }),
    [],
  );

  const fetchCMS = async () => {
    setLoading(true);
    const res = await AsyncGetApiCall(`/pages/view/${id}`);
    if (res?.errors) {
      toast.error(res.errors[0] || "Failed to fetch CMS");
      navigate("/cms-management");
    } else if (res?.success && res?.data) {
      setFormData({
        title: res.data.title || "",
        slug: res.data.slug || "",
        description: res.data.description || "",
      });
    } else {
      toast.error(res?.message || "Failed to fetch CMS");
      navigate("/cms-management");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (id) {
      fetchCMS();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title.trim(),
      slug: formData.slug.trim(),
      description: formData.description?.trim(),
    };

    if (!payload.title) return toast.error("Title is required");
    if (!payload.slug) return toast.error("Slug is required");
    if (!payload.description) return toast.error("Description is required");

    setLoading(true);
    const res = await AsyncPatchApiCall(`/pages/update/${id}`, payload);
    if (res?.errors) {
      toast.error(res.errors[0] || "Update failed");
    } else if (res?.success) {
      toast.success(res.message || "CMS updated successfully");
      navigate(`/cms-management/view/${id}`);
    } else {
      toast.error(res?.message || "Update failed");
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24">
          <h6 className="text-lg fw-semibold mb-0">Update CMS</h6>
        </div>
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Slug *</label>
              <input
                type="text"
                className="form-control"
                value={formData.slug}
                readOnly
              />
              <small className="text-secondary-light">
                Slug cannot be edited while updating CMS.
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label">Description *</label>
              <JoditEditor
                ref={editor}
                value={formData.description}
                config={editorConfig}
                onBlur={(newContent) =>
                  setFormData((prev) => ({ ...prev, description: newContent }))
                }
                onChange={() => {}}
              />
            </div>

            <div className="d-flex align-items-center gap-2">
              <button type="submit" className="btn btn-primary-600">
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/cms-management")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CMSUpdateLayer;
